"use client";

import cn from "@utils/cn";
import { slugify } from "@utils/string";
import { useTranslations } from "next-intl";
import { FC } from "react";

import Icon from "@/components/ui/Icon/Icon";
import { contactUs } from "@/dataLayer/dataLayer";
import { OfficeProps } from "@/types";

type OfficeBoxProps = {
  item: OfficeProps;
};

const OfficeBox: FC<OfficeBoxProps> = ({ item }) => {
  const t = useTranslations();
  return (
    <div className={cn("bg-gray-7 rounded p-l text-s", item.departmentArea ? "md:col-span-2" : "col-span-1")}>
      <h3 className="mb-s font-primary font-medium uppercase">{item.title}</h3>

      <div className="flex flex-col md:flex-row">
        {item.departmentArea && (
          <div className="mt-l md:w-7/12">
            <div className="flex items-start gap-xxs">
              <Icon name="alert-2" size="20" title="Departments" className="stroke-black" />
              <div className="font-medium flex flex-col gap-xxs">
                <p className="p-3xs">Departments:</p>
                <ul className="md:columns-2 text-s font-secondary-light [&>li]:inline-flex [&>li]:pr-xxs [&>li]:md:flex [&>li]:md:before:content-['\2022'] [&>li]:md:before:mr-xs">
                  {item.departmentArea.map((area, index, item) => (
                    <li key={slugify(area)}>
                      {area}
                      {index !== item.length - 1 ? ";" : " "}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="mt-l">
          <p className="mb-s">
            <a href={`tel:${item.phoneNumber}`} className="flex items-center gap-xs font-medium">
              <Icon name="phone" size="20" title="phone" className="stroke-black" /> {item.phoneNumber}
            </a>
          </p>
          {item.email && (
            <p className="mb-s">
              <a href={`to:${item.email}`} className="flex items-center gap-xs font-medium underline underline-offset-2">
                <Icon name="mail" size="20" title="email" className="stroke-black" /> {item.email}
              </a>
            </p>
          )}
          {item.position && (
            <div className="flex items-start gap-xs">
              <Icon name={item.position.icon || "geotag"} size="20" title={item.position.title} className="stroke-black" />
              <div className="font-medium flex flex-col gap-xxs">
                <p>{item.position.title}</p>
                <p className="font-normal">{item.location}</p>
                <p>
                  <a
                    href={`https://www.google.com/maps/place/${item.position.coordinates?.lat},${item.position.coordinates?.lon}`}
                    className="underline underline-offset-2"
                    onClick={() => contactUs({ cta_name: t("label.common.getDirection"), title_text: item.title, cta_location: item.location || "" })}
                  >
                    {t("label.common.getDirection")}
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OfficeBox;
